/**
 * TABLE MANAGER - Rendering Tabella Ottimizzato
 * Sistema di tabella ad alte performance con virtual scrolling
 */

import { eventManager, Events } from '../../core/events.js';
import { getRoleChipClass } from '../filters/index.js';
import { renderStudioActionButton } from '../../ui/action-buttons.js';
import { playerToKey } from '../../utils/playerKey.js';
import { buildPlayerNameCellHtml } from '../../utils/teamLogos.js';
import { escapeHtml } from '../../utils/escapeHtml.js';
import { formatFvmValue } from '../../utils/formatFvm.js';
console.log('[TableManager] eventManager ID:', eventManager?._id);
import { log } from '../../utils/logger.js';

const MOBILE_CARDS_MQ = '(max-width: 760px)';
const PAGE_SIZE = 10;

class TableManager {
  constructor(containerSelector) {
    this.container = document.querySelector(containerSelector);
    this.tbody = this.container?.querySelector('tbody');
    
    this.players = [];
    this.visibleRows = PAGE_SIZE;
    this.currentPage = 1;
    this.sortColumn = 'fvm';
    this.sortDirection = 'desc';
    
    this.isRendering = false;
    this.renderPending = false;
    this._sortedPlayers = null;
    this._sortCacheKey = '';
    
    // Performance tracking
    this.lastRenderTime = 0;
    this.renderCount = 0;
    
    // Container card per la vista mobile (renderizzato accanto alla tabella,
    // mostrato solo su telefono tramite CSS). Riusa gli stessi handler azioni.
    this.tableWrap = this.container?.closest('.players-table-container') || this.container?.parentElement || null;
    this.cardsContainer = null;
    this._cardsMq = window.matchMedia(MOBILE_CARDS_MQ);
    this._onCardsMqChange = () => this.syncCardsViewMode();

    this.initializeEventListeners();
    
    // Setup azioni giocatori DOPO aver impostato il container
    if (this.container) {
      this.setupCardsContainer();
    }
    
    log.info('Table: Inizializzata', { container: containerSelector });
  }

  /**
   * Inizializza event listeners
   */
  initializeEventListeners() {
    // Ascolta aggiornamenti filtri
    eventManager.on(Events.TABLE_UPDATE, (data) => {
      this.updatePlayers(data.players);
    });

    // Ascolta eventi ordinamento
    eventManager.on(Events.TABLE_SORT, (data) => {
      this.setSorting(data.column, data.direction);
    });
  }

  /**
   * Crea (in modo idempotente) il contenitore delle card mobile e collega
   * la delega eventi riusando gli stessi handler dei pulsanti tabella.
   */
  setupCardsContainer() {
    if (!this.tableWrap) return;

    let cards = this.tableWrap.querySelector('.players-cards');
    if (!cards) {
      cards = document.createElement('div');
      cards.className = 'players-cards';
      cards.setAttribute('data-testid', 'players-cards');
      // Inserisci subito dopo la tabella, dentro lo stesso wrapper
      if (this.container && this.container.parentElement === this.tableWrap) {
        this.container.insertAdjacentElement('afterend', cards);
      } else {
        this.tableWrap.appendChild(cards);
      }

      // Delega click sui pulsanti azione delle card (stesse classi della tabella)
    }

    this.cardsContainer = cards;
    this._cardsMq.addEventListener('change', this._onCardsMqChange);
    this.syncCardsViewMode();
  }

  usesMobileCardsView() {
    return this._cardsMq.matches;
  }

  syncCardsViewMode() {
    if (!this.cardsContainer) return;
    const mobile = this.usesMobileCardsView();
    this.cardsContainer.hidden = !mobile;
    if (!mobile) {
      this.cardsContainer.innerHTML = '';
      return;
    }
    if (this.players.length > 0) {
      const pageData = this.getPagePlayers();
      this.renderCards(pageData);
      // Come in render(): senza TABLE_RENDER le card restano con shell azioni vuote
      eventManager.emit(Events.TABLE_RENDER, {
        players: pageData,
        page: this.currentPage,
        total: this.players.length,
      });
    }
  }
  
  /**
   * Aggiorna i dati dei giocatori
   */
  updatePlayers(players) {
    this.players = players || [];
    this._invalidateSortCache();
    log.debug('Table: Dati aggiornati', this.players.length);
    
    // Resetta alla prima pagina quando cambiano i dati
    this.currentPage = 1;
    
    // Renderizza con throttling
    this.queueRender();
  }

  /**
   * Imposta ordinamento
   */
  setSorting(column, direction = null) {
    // Toggle direction se stessa colonna
    if (this.sortColumn === column && !direction) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = direction || 'desc';
    }
    
    log.debug('Table: Ordinamento', { column, direction: this.sortDirection });
    this._invalidateSortCache();
    this.queueRender();
  }

  _invalidateSortCache() {
    this._sortedPlayers = null;
    this._sortCacheKey = '';
  }

  _getSortCacheKey() {
    return `${this.players.length}:${this.sortColumn}:${this.sortDirection}`;
  }

  /**
   * Ordina i giocatori
   */
  sortPlayers() {
    const cacheKey = this._getSortCacheKey();
    if (this._sortedPlayers && this._sortCacheKey === cacheKey) {
      return this._sortedPlayers;
    }

    if (!this.sortColumn) {
      this._sortedPlayers = this.players;
      this._sortCacheKey = cacheKey;
      return this._sortedPlayers;
    }

    const sorted = [...this.players].sort((a, b) => {
      let valueA = a[this.sortColumn];
      let valueB = b[this.sortColumn];
      
      // Gestione valori null/undefined
      if (valueA === null || valueA === undefined) valueA = '';
      if (valueB === null || valueB === undefined) valueB = '';
      
      // Conversione a numeri se applicabile
      if (!isNaN(valueA) && !isNaN(valueB)) {
        valueA = parseFloat(valueA) || 0;
        valueB = parseFloat(valueB) || 0;
      } else {
        valueA = String(valueA).toLowerCase();
        valueB = String(valueB).toLowerCase();
      }
      
      const comparison = valueA < valueB ? -1 : valueA > valueB ? 1 : 0;
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this._sortedPlayers = sorted;
    this._sortCacheKey = cacheKey;
    return sorted;
  }

  /**
   * Ottieni giocatori per la pagina corrente
   */
  getPagePlayers() {
    const sortedPlayers = this.sortPlayers();
    const startIndex = (this.currentPage - 1) * this.visibleRows;
    const endIndex = startIndex + this.visibleRows;
    
    return sortedPlayers.slice(startIndex, endIndex);
  }

  /**
   * Renderizza la tabella (OTTIMIZZATO)
   */
  render() {
    if (!this.tbody || this.isRendering) return;
    
    this.isRendering = true;
    log.time('Table: Rendering');
    
    const pageData = this.getPagePlayers();
    
    // Usa DocumentFragment per performance
    const fragment = document.createDocumentFragment();
    
    pageData.forEach((player, index) => {
      const row = this.createPlayerRow(player, index);
      fragment.appendChild(row);
    });
    
    // Sostituisci tutto il contenuto in una volta
    this.tbody.innerHTML = '';
    this.tbody.appendChild(fragment);

    if (this.usesMobileCardsView()) {
      this.renderCards(pageData);
    } else if (this.cardsContainer) {
      this.cardsContainer.innerHTML = '';
      this.cardsContainer.hidden = true;
    }
    
    // Aggiorna header ordinamento
    this.updateSortingHeaders();
    
    // Aggiorna contatori
    this.updateCounters();
    
    this.isRendering = false;
    this.lastRenderTime = Date.now();
    this.renderCount++;
    
    log.timeEnd('Table: Rendering');
    log.debug('Table: Renderizzata', {
      righe: pageData.length,
      pagina: this.currentPage,
      render: this.renderCount
    });
    
    // Emetti evento completamento
    eventManager.emit(Events.TABLE_RENDER, {
      players: pageData,
      page: this.currentPage,
      total: this.players.length
    });
  }

  /**
   * Crea una riga giocatore (OTTIMIZZATA) - senza preferiti
   */
  createPlayerRow(player, index) {
    const row = document.createElement('tr');
    row.className = 'player-row';
    row.dataset.playerId = player.id || index;
    
    // Crea celle ottimizzate
    row.innerHTML = this.createRowHTML(player);
    
    return row;
  }

  /**
   * Normalizza un ruolo per visualizzazione corretta
   */
  normalizeRoleForDisplay(role) {
    if (!role) return 'N/A';
    return String(role).toUpperCase();
  }

  /**
   * Genera HTML per ruoli multipli
   */
  createRoleChipsHTML(player) {
    // *** DEBUG: Log per vedere i dati dei ruoli ***
    if (player.name === 'Orsolini' || (player.roles && Array.isArray(player.roles) && player.roles.length > 1)) {
      console.log(`[ROLES] ${player.name}: role='${player.role}', roles=[${player.roles ? player.roles.join(',') : 'none'}]`);
    }
    
    // Gestisce sia ruoli multipli che singoli
    if (player.roles && Array.isArray(player.roles) && player.roles.length > 1) {
      // Ruoli multipli - crea chip separati
      const chips = player.roles.map((role, index) => {
        const normalizedRole = this.normalizeRoleForDisplay(role);
        return `<span class="${getRoleChipClass(role)}" title="Ruolo ${index + 1}: ${normalizedRole}">${normalizedRole}</span>`;
      }).join('');
      
      // Aggiungi classe CSS per ruoli multipli (2+) per browser senza :has()
      const containerClass = player.roles.length >= 2 ? 'roles-container multiple-roles' : 'roles-container';
      
      // Debug log
      if (player.roles.length >= 3) {
        console.log(`[TableManager] Giocatore con ${player.roles.length} ruoli: ${player.name} - Ruoli: [${player.roles.join(', ')}] - Classe: ${containerClass}`);
      }
      
      return `<div class="${containerClass}">${chips}</div>`;
    } else {
      // Ruolo singolo
      const role = player.role || 'N/A';
      const normalizedRole = this.normalizeRoleForDisplay(role);
      return `<div class="roles-container"><span class="${getRoleChipClass(role)}" title="Ruolo: ${normalizedRole}">${normalizedRole}</span></div>`;
    }
  }

  studioPlayerMeta(player) {
    const playerKey = playerToKey(player);
    return {
      playerKey,
      name: player.name || '',
      team: player.team || '',
    };
  }

  astaActionsShellHtml(player) {
    const { playerKey, name, team } = this.studioPlayerMeta(player);
    return `<div class="asta-actions-container pc-asta" data-player-key="${playerKey}" data-player-name="${name}" data-player-team="${team}"></div>`;
  }

  studioActionHtml(player) {
    const { playerKey, name, team } = this.studioPlayerMeta(player);
    const targets = window.appState?.studioTargets;
    const inStudio = targets?.has(playerKey) ?? false;
    const target = inStudio ? targets.get(playerKey) : undefined;
    // Shell prep; syncStudioButtonsInTable monta controlli o readonly in live
    return renderStudioActionButton({
      playerKey,
      name,
      team,
      inStudio,
      target,
      auctionMode: 'prep',
    });
  }

  /**
   * Genera HTML riga (template ottimizzato) - senza preferiti
   */
  createRowHTML(player) {
    const { playerKey, name, team } = this.studioPlayerMeta(player);
    return `
      <td class="col-role">
        ${this.createRoleChipsHTML(player)}
      </td>
      <td class="col-name" title="${escapeHtml(player.name || 'N/A')}">${buildPlayerNameCellHtml(player.team, player.name)}</td>
      <td class="col-quot">${formatFvmValue(player.fvm)}</td>
      <td class="col-fm">${this.formatStatValue(player.has_season_stats ? player.fm_score : null)}</td>
      <td class="col-matches">${this.formatSeasonStat(player, 'matches')}</td>
      <td class="col-goals">${this.formatSeasonStat(player, 'goals')}</td>
      <td class="col-assists">${this.formatSeasonStat(player, 'assists')}</td>
      <td class="col-yellow-cards">${this.formatSeasonStat(player, 'yellow_cards')}</td>
      <td class="col-red-cards">${this.formatSeasonStat(player, 'red_cards')}</td>
      <td class="col-actions">
        <div class="action-buttons-container" data-player-key="${playerKey}" data-player-name="${name}" data-player-team="${team}">
          ${this.studioActionHtml(player)}
        </div>
      </td>
    `;
  }

  /**
   * Renderizza la vista a card (mobile) per la pagina corrente.
   * Usa gli stessi dati e gli stessi pulsanti azione della tabella.
   */
  renderCards(pageData) {
    if (!this.cardsContainer || !this.usesMobileCardsView()) return;

    this.cardsContainer.hidden = false;

    if (!pageData || pageData.length === 0) {
      this.cardsContainer.innerHTML =
        '<div class="players-cards-empty">Nessun giocatore da mostrare</div>';
      return;
    }

    const startIndex = (this.currentPage - 1) * this.visibleRows;
    this.cardsContainer.innerHTML = pageData
      .map((player, index) => this.createCardHTML(player, startIndex + index))
      .join('');
  }

  /**
   * Genera l'HTML di una singola card giocatore (vista mobile).
   */
  createCardHTML(player, absoluteIndex) {
    const rank = String(absoluteIndex + 1).padStart(2, '0');
    const { playerKey, name, team } = this.studioPlayerMeta(player);

    const stat = (label, value) =>
      `<span class="pc-stat"><i>${label}</i><b>${this.formatStatValue(value)}</b></span>`;

    return `
      <article class="player-card" data-player-id="${player.id || absoluteIndex}">
        <span class="pc-rank">${rank}</span>
        <div class="pc-role">${this.createRoleChipsHTML(player)}</div>
        <div class="pc-id">${buildPlayerNameCellHtml(player.team, player.name, { variant: 'card' })}</div>
        <div class="pc-num">
          <span class="pc-quot">${formatFvmValue(player.fvm)}</span>
          <span class="pc-fm">FM ${this.formatStatValue(player.has_season_stats ? player.fm_score : null)}</span>
        </div>
        <div class="pc-actions" data-player-key="${playerKey}" data-player-name="${name}" data-player-team="${team}">
          ${this.studioActionHtml(player)}
        </div>
        <div class="pc-stats">
          ${[
            stat('P', player.has_season_stats ? player.matches : null),
            stat('G', player.has_season_stats ? player.goals : null),
            stat('A', player.has_season_stats ? player.assists : null),
            stat('Amm', player.has_season_stats ? player.yellow_cards : null),
            stat('Esp', player.has_season_stats ? player.red_cards : null),
          ].join('')}
        </div>
      </article>`;
  }

  /**
   * Formatta valori statistici
   */
  formatStatValue(value) {
    if (value === null || value === undefined) return '-';
    return String(value);
  }

  /** Partite/Gol/Assist/cartellini: solo se hydratati da stagione 25/26 */
  formatSeasonStat(player, key) {
    if (!player?.has_season_stats) return '-';
    return this.formatStatValue(player[key]);
  }

  /**
   * Aggiorna header ordinamento
   */
  updateSortingHeaders() {
    // Reset tutte le intestazioni
    const headers = this.container.querySelectorAll('th[data-sortable]');
    headers.forEach(h => h.dataset.direction = 'none');
    
    // Imposta header attiva
    const activeHeader = this.container.querySelector(`th[data-column="${this.sortColumn}"]`);
    if (activeHeader) {
      activeHeader.dataset.direction = this.sortDirection;
    }
  }

  /**
   * Aggiorna contatori e controlli paginazione
   */
  updateCounters() {
    const countElement = document.querySelector('[data-testid="player-count"]');
    if (countElement) {
      const startIndex = (this.currentPage - 1) * this.visibleRows;
      const visibleCount = Math.min(this.visibleRows, Math.max(0, this.players.length - startIndex));
      const totalFiltered = this.players.length;
      const totalPlayers = window.appState?.allPlayers?.length || totalFiltered;
      
      let text;
      if (totalFiltered === totalPlayers) {
        text = `Caricati ${totalPlayers} giocatori`;
      } else {
        text = `Mostrati ${totalFiltered} di ${totalPlayers} giocatori`;
      }
      
      countElement.textContent = text;
    }
    
    // Aggiorna controlli paginazione
    this.updatePaginationControls();
  }
  
  /**
   * Aggiorna i controlli di paginazione
   */
  updatePaginationControls() {
    const totalPages = Math.max(1, Math.ceil(this.players.length / this.visibleRows) || 1);
    const startItem = this.players.length === 0
      ? 0
      : ((this.currentPage - 1) * this.visibleRows) + 1;
    const endItem = Math.min(this.currentPage * this.visibleRows, this.players.length);

    document.querySelectorAll('[data-testid="pagination-current"]').forEach((el) => {
      el.textContent = `Pagina ${this.currentPage}`;
    });
    document.querySelectorAll('[data-testid="pagination-total"]').forEach((el) => {
      el.textContent = `di ${totalPages}`;
    });
    document.querySelectorAll('[data-testid="pagination-range"]').forEach((el) => {
      el.textContent = this.players.length === 0
        ? '(Nessun giocatore)'
        : `(Mostra ${startItem}–${endItem} di ${this.players.length})`;
    });

    document.querySelectorAll('[data-testid="pagination-prev"]').forEach((btn) => {
      btn.disabled = this.currentPage <= 1;
    });
    document.querySelectorAll('[data-testid="pagination-next"]').forEach((btn) => {
      btn.disabled = this.currentPage >= totalPages || this.players.length === 0;
    });

    this.generatePageNumbers(totalPages);
  }

  /**
   * Genera i numeri di pagina
   */
  generatePageNumbers(totalPages) {
    document.querySelectorAll('[data-testid="pagination-pages"]').forEach((pagesContainer) => {
      pagesContainer.innerHTML = '';

      const maxVisible = 7;
      let startPage = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
      let endPage = Math.min(totalPages, startPage + maxVisible - 1);

      if (endPage - startPage + 1 < maxVisible) {
        startPage = Math.max(1, endPage - maxVisible + 1);
      }

      for (let i = startPage; i <= endPage; i++) {
        const pageBtn = document.createElement('button');
        pageBtn.type = 'button';
        pageBtn.className = `pagination-page ${i === this.currentPage ? 'active' : ''}`;
        pageBtn.textContent = String(i);
        pageBtn.addEventListener('click', () => this.setPage(i));
        pagesContainer.appendChild(pageBtn);
      }
    });
  }

  /**
   * Sistema di rendering con coda (throttling)
   */
  queueRender() {
    if (this.isRendering) {
      this.renderPending = true;
      return;
    }

    requestAnimationFrame(() => {
      this.render();

      if (this.renderPending) {
        this.renderPending = false;
        setTimeout(() => this.queueRender(), 50);
      }
    });
  }

  /**
   * Imposta pagina
   */
  setPage(page) {
    this.currentPage = Math.max(1, page);
    this.queueRender();
  }

  /**
   * Prossima pagina
   */
  nextPage() {
    const maxPage = Math.ceil(this.players.length / this.visibleRows);
    if (this.currentPage < maxPage) {
      this.setPage(this.currentPage + 1);
    }
  }

  /**
   * Pagina precedente
   */
  prevPage() {
    if (this.currentPage > 1) {
      this.setPage(this.currentPage - 1);
    }
  }

  /**
   * Pulisce la tabella e resetta lo stato
   */
  clear() {
    // Pulisci tabella
    if (this.tbody) {
      this.tbody.innerHTML = '';
    }

    // Pulisci card mobile
    if (this.cardsContainer) {
      this.cardsContainer.innerHTML = '';
    }
    
    // Reset stato
    this.players = [];
    this.currentPage = 1;
    this.sortColumn = 'fvm';
    this.sortDirection = 'desc';
    this.renderCount = 0;
    this.lastRenderTime = 0;
    
    // Pulisci contatori
    const countElement = document.querySelector('[data-testid="player-count"]');
    if (countElement) {
      countElement.textContent = 'Nessun giocatore caricato';
    }
    
    log.info('Table: Pulita e resettata');
  }
}

export { TableManager };
